import React from 'react';
import BigNumber from 'bignumber.js';
import InputLabel from '../base/InputLabel';
import NumberInput from '../base/NumberInput';
import InfoHint from '../common/InfoHint';
import { buildTpExits } from '../../utils/multiExits';

// Shared "Multiple take-profits" settings section — used by every market's full
// SettingsModal AND mirrors the Pocket settings sheet. Lets the user switch
// between a single TP and a 2–5 level scale-out, set the allocation mode and
// each exit's allocation + net (optimized) R-multiple.
const pill = (on) =>
  `px-2.5 py-1 rounded-full text-xs transition-colors ${on
    ? 'bg-primary-orange/20 text-primary-orange'
    : 'hover:bg-slate-800 text-slate-500'}`;

// onChange(partial) merges a partial values update — each modal supplies the
// adapter matching its own update style (functional spread vs partial-merge).
const MultiTpSettings = ({ values = {}, onChange }) => {
  const enabled = !!values.multiTpEnabled;
  const count = values.tpCount || 3;

  const setEnabled = (v) => onChange({ multiTpEnabled: v });
  const setCount = (n) => onChange({ tpCount: n, tpExits: buildTpExits(n) });
  const setExit = (i, field, val) => {
    const base = Array.isArray(values.tpExits) ? [...values.tpExits] : [];
    while (base.length < count) {
      base.push({ alloc: new BigNumber(0), rMultiple: new BigNumber(1.5 + base.length) });
    }
    base[i] = { ...base[i], [field]: val || new BigNumber(0) };
    onChange({ tpExits: base });
  };

  // Render exactly `count` rows, padding from defaults if persisted state is short.
  const rowExits = Array.from({ length: count }, (_, i) =>
    (Array.isArray(values.tpExits) && values.tpExits[i]) || { alloc: new BigNumber(0), rMultiple: new BigNumber(1.5 + i) });
  const allocSum = rowExits.reduce((a, e) => {
    const v = e?.alloc;
    return a + (v && v.toNumber ? v.toNumber() : Number(v) || 0);
  }, 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-1.5">
          <InputLabel>Take-Profits</InputLabel>
          <InfoHint text="Split the position across 2–5 exits. Each target's R is net — fees included (the optimized take); the calculator shows only the optimized price." />
        </span>
        <div className="flex gap-1">
          <button onClick={() => setEnabled(false)} className={pill(!enabled)}>Single</button>
          <button onClick={() => setEnabled(true)} className={pill(enabled)}>Multiple</button>
        </div>
      </div>

      {enabled && (
        <div className="rounded-lg border border-white/5 p-4 space-y-4">
          <div>
            <InputLabel>Exits</InputLabel>
            <div className="flex gap-1 mt-1">
              {[2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setCount(n)} className={pill(count === n)}>{n}</button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {rowExits.map((e, i) => (
              <div key={i} className="grid grid-cols-[2.5rem_1fr_1fr] items-end gap-3">
                <div className="text-slate-400 text-sm pb-2.5">TP{i + 1}</div>
                <div>
                  {i === 0 && <InputLabel>Allocation</InputLabel>}
                  <NumberInput
                    value={e.alloc}
                    onChange={(v) => setExit(i, 'alloc', v)}
                    suffix="%"
                    decimals={0}
                    min={new BigNumber(0)}
                  />
                </div>
                <div>
                  {i === 0 && <InputLabel>Target (net R)</InputLabel>}
                  <NumberInput
                    value={e.rMultiple}
                    onChange={(v) => setExit(i, 'rMultiple', v)}
                    suffix="R"
                    decimals={2}
                    min={new BigNumber(0.1)}
                  />
                </div>
              </div>
            ))}
          </div>

          <p className={`text-xs ${Math.abs(allocSum - 100) < 0.5 ? 'text-emerald-400/80' : 'text-amber-400/90'}`}>
            {allocSum < 100
              ? `Allocated ${allocSum}% · runner ${100 - allocSum}% rides the rest`
              : allocSum > 100
                ? `Allocated ${allocSum}% — over 100%`
                : 'Allocated 100%'}
          </p>
        </div>
      )}
    </div>
  );
};

export default MultiTpSettings;
